
import React, { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import SavedProperties from "../components/SavedProperties";
import RentalRequests from "../components/RentalRequests";
import UserProfile from "../components/UserProfile";
import AdminNotifications from "../components/AdminNotifications";
import NavigationHeader from "../components/NavigationHeader";
import Footer from "../components/Footer";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { LogOut, User, Bell } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import RouteGuard from "../components/RouteGuard";
import NotificationCenter from "../components/notifications/NotificationCenter";
import { useToast } from "@/hooks/use-toast";

const TABS = [
  { label: "العقارات المحفوظة", key: "saved" },
  { label: "طلبات الإيجار", key: "requests" },
  { label: "الإشعارات", key: "notifications" },
  { label: "ملفي الشخصي", key: "profile" }
];

const UserDashboardContent = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { toast } = useToast();
  
  const currentTab = searchParams.get('tab') || 'saved';
  const [tenantId, setTenantId] = useState<string>('');

  useEffect(() => {
    const fetchTenantId = async () => {
      try {
        const { data: { user } } = await supabase.auth.getUser();
        if (!user) return;

        const { data: tenantData } = await supabase
          .from('tenants')
          .select('id')
          .eq('user_id', user.id)
          .single();

        if (tenantData) {
          setTenantId(tenantData.id);
        }
      } catch (error) {
        console.error('Error fetching tenant ID:', error);
      }
    };

    fetchTenantId();
  }, []);

  const handleLogout = async () => {
    await supabase.auth.signOut();
    navigate('/');
  };

  const handleNotificationClick = () => {
    navigate('/notifications?type=tenant');
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-blue-50 to-white font-['Tajawal',sans-serif] flex flex-col">
      <NavigationHeader />
      
      <div className="flex-1">
        <div className="container mx-auto px-4 py-8">
          <div className="max-w-4xl mx-auto">
            <Card>
              <CardHeader>
                <CardTitle className="text-2xl font-bold text-gray-900 text-right">لوحة تحكم المستخدم (الباحث)</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex flex-row-reverse justify-end gap-2 mb-4">
                  {TABS.map(t => (
                    <button
                      key={t.key}
                      onClick={() => navigate(`/user-dashboard?tab=${t.key}`)}
                      className={`px-4 py-2 rounded-t-lg font-bold text-md ${
                        currentTab === t.key
                          ? "bg-blue-700 text-white"
                          : "bg-gray-100 text-blue-900"
                      }`}
                    >
                      {t.label}
                    </button>
                  ))}
                </div>
                <div className="pt-2">
                  {currentTab === "saved" && <SavedProperties />}
                  {currentTab === "requests" && <RentalRequests />}
                  {currentTab === "notifications" && tenantId && (
                    <AdminNotifications userType="tenant" userId={tenantId} />
                  )}
                  {currentTab === "profile" && <UserProfile />}
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
};

const UserDashboard = () => {
  return (
    <RouteGuard requiredUserType="tenant" redirectTo="/login">
      <UserDashboardContent />
    </RouteGuard>
  );
};

export default UserDashboard;
